import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/dragdrop/x-drag-drop.js';
import type { XDropdownSearchSelection } from '@adornis/popover/x-dropdown-search-selection.js';
import { DurchfuehrungsTag } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import '@chancenwerk/client/components/chancen-component-title.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { KPFPublicInformationAPI } from '@chancenwerk/shared/public-access/api/KPFPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { takeUntil, tap } from 'rxjs';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import '../components/board-component-course-aggregated-detail-card.js';
import '../components/board-component-course-detail-card.js';
import '../components/board-component-drag-drop.js';
import '../components/board-component-user-card.js';
import '../components/board-component-user-pool.js';
import { BoardRoutes } from '../routes.js';

const substituteKpfSelector: BaseQLSelectionSet<KPF> = {
  school: { _class: 1, _id: 1, name: 1, kuerzel: 1 },
  kuerzel: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  kpf: 1,
  schoolID: 1,
  school: { _class: 1, _id: 1, name: 1, kuerzel: 1 },
  schuKoIDs: 1,
  accessTokenForExternals: 1,
  accessTokenForStudents: 1,
  kuerzel: 1,
  substituteKPFIDs: 1,
  substituteKPFs: substituteKpfSelector,
  schuKos: { _id: 1, _class: 1, vorname: 1, nachname: 1, email: 1, employeeID: 1 },
  produkt: {
    _class: 1,
    kuerzel: 1,
    USLernFoerderung: 1,
    OSLernFoerderung: 1,
    uebungsLeiterProLernFoerderung: 1,
    durchfuehrungsTage: DurchfuehrungsTag.allFields,
  },
};

/**
 * @customElement board-page-kpf-management
 */
@customElement('board-page-kpf-management')
export class BoardPageCourseDetails extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'Lernförderung';
    this.backRoute = BoardRoutes.overview;
    this.permission = ChancenPermission.board.MANAGE_KPF_BOARD;
  }

  @property() public kpfID = new RXController(this, '');
  @state() private readonly _kpf = new RXController<Maybe<KPF>>(this, null);
  @state() private readonly _kpfs = new RXController<Maybe<KPF[]>>(this, null);

  override connectedCallback(): void {
    super.connectedCallback();

    this.kpfID.observable.pipe(takeUntil(this.disconnected)).subscribe(async id => {
      const kpf = await KPF.getByID<KPF>(id ?? '')(kpfSelector);
      this._kpf.next(kpf);
    });

    this._kpf.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(kpf => {
          this.label = `KPF: ${kpf?.kuerzel ?? ''}`;
        }),
      )
      .subscribe();

    KPFPublicInformationAPI.getAll()({ produkt: { kuerzel: 1 }, schoolID: 1, school: { name: 1, kuerzel: 1 } }).then(
      kpfs => {
        this._kpfs.next(kpfs);
      },
    );
  }

  override header() {
    return html`
      <cr-flex space="sm" horizontal>
        <cr-button
          mode="action"
          @click=${async () => {
            await ChromiumDialog.waitFor(this._saveAll());
          }}
        >
          Speichern
        </cr-button>
      </cr-flex>
    `;
  }

  inputRef: Ref<XDropdownSearchSelection<string>> = createRef();

  override content() {
    if (this._kpfs.value === null) {
      return html`<cr-flex padding="lg" crossaxis-center><cr-loader></cr-loader></cr-flex>`;
    }

    return html`
      <cr-flex padding="sm">
        <cr-card padding="md" space="sm">
          <chancen-component-title
            text="LeKo Vertretungs-KPF"
            subtext="Welche KPFs dürfen LeKos als Vertretung stellen"
            icon="accessibility"
          ></chancen-component-title>
          <cr-dropdown-search-selection
            slot="right"
            clearable
            flex
            icon="search"
            placeholder="KPF verlinken"
            .search=${(search: Maybe<string>) => {
              return this._kpfs.value?.filter(kpf => {
                if (!search) return true;
                return kpf.toString()?.toLowerCase().includes(search.toLowerCase());
              });
            }}
            .renderString=${(entry: Maybe<KPF>) => {
              if (!entry) return null;
              return `${entry.toString()}`;
            }}
            @value-picked=${async (e: CustomEvent<{ value: KPF }>) => {
              setTimeout(() => this.inputRef.value?.value.next(''), 250);
              await this._addSubstituteKpf(e.detail.value._id);
            }}
            ${ref(this.inputRef)}
          ></cr-dropdown-search-selection>
          <cr-text bold ${css({ marginTop: this.spacing.md })}>Bereits verlinkte KPFs</cr-text>
          ${(this._kpf.value?.substituteKPFIDs ?? []).length === 0
            ? html`<cr-text ${css({ opacity: 0.7 })}>noch keine...</cr-text>`
            : html`<cr-flex ${css({ paddingTop: this.spacing.md })} horizontal space="md" wrap>
                ${repeat(
                  this._kpf.value?.substituteKPFIDs ?? [],
                  kpfID => kpfID,
                  kpfID => {
                    const kpf = this._kpfs.value?.find(_kpf => _kpf._id === kpfID);
                    if (!kpf) return nothing;
                    return html`<cr-card space="md" raised ${css({ maxWidth: '500px' })}>
                      <cr-flex horizontal crossaxis-center space="md">
                        <cr-text>${kpf.toString()}</cr-text>
                        <cr-button
                          tone="error"
                          mode="icon"
                          icon="delete"
                          @click=${async () => this._removeSubstituteKpf(kpf._id)}
                          >Löschen</cr-button
                        >
                      </cr-flex>
                    </cr-card>`;
                  },
                )}
              </cr-flex>`}
        </cr-card>
      </cr-flex>
    `;
  }

  private async _addSubstituteKpf(id: Maybe<string>) {
    if (!id || !this._kpf.value) {
      return;
    }

    if (id === this.kpfID.value || (this._kpf.value.substituteKPFIDs ?? []).includes(id)) {
      return;
    }

    this._kpf.value.substituteKPFIDs ??= [];
    this._kpf.value.substituteKPFIDs.push(id);

    this._kpf.next(this._kpf.value);
  }

  private _removeSubstituteKpf(id: Maybe<string>) {
    if (!id || !this._kpf.value) {
      return;
    }

    this._kpf.value.substituteKPFIDs = (this._kpf.value.substituteKPFIDs ?? []).filter(_id => _id !== id);
    this._kpf.value.substituteKPFs = (this._kpf.value.substituteKPFs ?? []).filter(kpf => kpf._id !== id);

    this._kpf.next(this._kpf.value);
  }

  private async _saveAll() {
    if (!this._kpf.value) {
      return;
    }

    await this._kpf.value.save();
  }
}
